/**
 * Rich message payloads (cards, carousels, chips) as stored on drafts and sent
 * messages. Browser-safe so the inbox can render what the customer saw.
 */
import { Option, Schema } from "effect";

const RcsMediaSchema = Schema.Struct({
  fileUrl: Schema.String,
  thumbnailUrl: Schema.optional(Schema.String),
  height: Schema.optional(Schema.Literal("SHORT", "MEDIUM", "TALL")),
});

const RcsSuggestionSchema = Schema.Union(
  Schema.Struct({
    kind: Schema.Literal("reply"),
    text: Schema.String,
    postbackData: Schema.optional(Schema.String),
  }),
  Schema.Struct({
    kind: Schema.Literal("openUrl"),
    text: Schema.String,
    url: Schema.String,
    postbackData: Schema.optional(Schema.String),
  }),
  Schema.Struct({
    kind: Schema.Literal("dial"),
    text: Schema.String,
    phoneNumber: Schema.String,
    postbackData: Schema.optional(Schema.String),
  }),
  Schema.Struct({
    kind: Schema.Literal("viewLocation"),
    text: Schema.String,
    latitude: Schema.Number,
    longitude: Schema.Number,
    label: Schema.optional(Schema.String),
    postbackData: Schema.optional(Schema.String),
  }),
  Schema.Struct({
    kind: Schema.Literal("shareLocation"),
    text: Schema.String,
    postbackData: Schema.optional(Schema.String),
  }),
  Schema.Struct({
    kind: Schema.Literal("calendarEvent"),
    text: Schema.String,
    startTime: Schema.String,
    endTime: Schema.String,
    title: Schema.String,
    description: Schema.optional(Schema.String),
    postbackData: Schema.optional(Schema.String),
  }),
);

const RcsCardSchema = Schema.Struct({
  title: Schema.optional(Schema.String),
  description: Schema.optional(Schema.String),
  media: Schema.optional(RcsMediaSchema),
  suggestions: Schema.optional(Schema.Array(RcsSuggestionSchema)),
});

/**
 * An RCS agent message body; mirrors `RcsMessageContent` in
 * redo-communication-common, which can't be imported here without a cycle.
 * Decodes untrusted stored JSON, so a writer picks the tagged `kind` openly.
 */
export const RcsMessageContentSchema = Schema.Union(
  Schema.Struct({
    kind: Schema.Literal("text"),
    text: Schema.String,
    suggestions: Schema.optional(Schema.Array(RcsSuggestionSchema)),
  }),
  Schema.Struct({
    kind: Schema.Literal("media"),
    media: RcsMediaSchema,
    suggestions: Schema.optional(Schema.Array(RcsSuggestionSchema)),
  }),
  Schema.Struct({
    kind: Schema.Literal("cards"),
    cards: Schema.NonEmptyArray(RcsCardSchema),
    orientation: Schema.optional(Schema.Literal("VERTICAL", "HORIZONTAL")),
    thumbnailAlignment: Schema.optional(Schema.Literal("LEFT", "RIGHT")),
    cardWidth: Schema.optional(Schema.Literal("SMALL", "MEDIUM")),
  }),
);
export type RcsMessageContentData = typeof RcsMessageContentSchema.Type;

/**
 * What a sent message's `message_sms.rich_content` holds. Tagged by `format`
 * so each channel keeps its own shape: Apple Messages for Business adds an
 * `apple_messages` member here rather than squeezing into the RCS one.
 */
export const MessageRichContent = Schema.Union(
  Schema.Struct({
    format: Schema.Literal("rcs"),
    content: RcsMessageContentSchema,
  }),
);
export type MessageRichContent = typeof MessageRichContent.Type;

/**
 * Read-side decode: a payload this build doesn't know (a newer format, a
 * malformed row) becomes null so the message falls back to its text instead of
 * failing the whole transcript.
 */
export const StoredMessageRichContent = Schema.transform(
  Schema.Unknown,
  Schema.NullOr(Schema.typeSchema(MessageRichContent)),
  {
    strict: true,
    decode: (raw) =>
      Option.getOrNull(Schema.decodeUnknownOption(MessageRichContent)(raw)),
    encode: (content) => content,
  },
);
